import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  SimpleGrid,
  Center,
} from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { useEffect } from 'react'
import { Spinner } from '@/components/atoms/Spinner'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { EXPLORERS } from '@/constants/constants'
import { GET_PLINKO_GAME } from '@/api/query'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { UserGameResult } from '../UserGameResult/UserGameResult'
import { MiniGameStatus } from '@/__generated__/graphql'
import { getTokenByDenom } from '@/services/token-service'
import { formatAmount } from '@/helpers/formatAmount'
import { useTranslation } from 'next-i18next/pages'

export default ({ onClose, gameId }: { onClose: () => void; gameId: string }) => {
  const { t } = useTranslation('common')
  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          {t('common.game-details', { gameId })}
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          <GameZoneGameDetails gameId={gameId} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const GameZoneGameDetails = ({ gameId }: { gameId: string }) => {
  const { t } = useTranslation('common')
  const { data, refetch, error } = useQuery(GET_PLINKO_GAME, {
    variables: { id: gameId },
  })

  useEffect(() => {
    refetch()
  }, [])

  if (error) {
    return <Text color={'ninja.200'}>{t('common.error-occurred')}</Text>
  }

  if (!data?.plinkoGame) {
    return (
      <Center>
        <Spinner />
      </Center>
    )
  }

  const game = data.plinkoGame
  const token = getTokenByDenom(game.denom)

  // game.bet is total bet amount, calculate bet per ball
  const totalBet = game.bet * game.balls
  const multiplier = (parseFloat(game.payout) / totalBet).toFixed(2)

  const result = game.payout > totalBet ? MiniGameStatus.Win : MiniGameStatus.Lose

  return (
    <>
      <UserGameResult
        address={game.player}
        multiplier={multiplier}
        payout={{ amount: game.payout, denom: game.denom }}
        result={result}
      />
      <SimpleGrid columnGap={3} columns={{ base: 1, sm: 2 }}>
        <StatsRow name={t('common.game-status')} value={<Text color={'brand.green'}>{t('common.finished')}</Text>} />
        <StatsRow
          name={t('plinko.total-bet')}
          value={
            <FiatOrCryptoAmount
              iconSize={'18px'}
              denom={game.denom}
              value={formatAmount(totalBet.toString(), { decimals: token.decimals })}
              wrapperStyles={{ fontSize: 'xs' }}
            />
          }
        />
      </SimpleGrid>

      <SimpleGrid columnGap={3} columns={{ base: 1, sm: 2 }} mb={4}>
        <StatsRow
          name={t('common.tx-hash')}
          value={
            <CopiableLink
              trimmedLength={12}
              maxLength={32}
              link={`${EXPLORERS.INJECTIVE_TRANSACTIONS}${game.txHash}/`}
              value={game.txHash}
            />
          }
        />
        <StatsRow name={t('common.date')} value={<Text>{new Date(game.updated).toLocaleString()}</Text>} />
      </SimpleGrid>
    </>
  )
}
